import { getWeekStart } from '@utils/dates'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useSearchParams as useSearchParamsImported } from 'react-router-dom'

const WEEK_START_KEY = 'week-start'

export const buildSearchParam = ({
  date,
  params = new URLSearchParams(),
  weekStartsOn,
}: {
  date: DateTime
  params?: URLSearchParams
  weekStartsOn: 'monday' | 'sunday'
}) => {
  params.set(WEEK_START_KEY, getWeekStart({ date, weekStartsOn }).toISODate())
  return params
}

const parseDate = (
  param: string | null,
  zone?: string,
): DateTime | undefined => {
  if (param === null) return undefined

  const parsed = DateTime.fromISO(param, { zone })

  return parsed.invalidReason === null ? parsed : undefined
}

export const useWeekDatesSearchParams = (
  options?: { zone?: string },
  useSearchParams = useSearchParamsImported, // injected for easier testing
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  return useMemo(() => {
    const weekStartParam = searchParams.get(WEEK_START_KEY)
    const start = parseDate(weekStartParam, options?.zone)

    const set = ({
      start,
      weekStartsOn,
    }: {
      start: DateTime | undefined
      weekStartsOn: 'sunday' | 'monday'
    }) => {
      if (!start) {
        setSearchParams((params) => {
          params.delete(WEEK_START_KEY)
          return params
        })
      } else {
        setSearchParams((params) =>
          buildSearchParam({ date: start, params, weekStartsOn }),
        )
      }
    }

    return {
      start,
      set,
      linkSearchParam: weekStartParam
        ? `?${WEEK_START_KEY}=${weekStartParam}`
        : '',
    }
  }, [searchParams, setSearchParams, options])
}
